import { SearchOutlined } from '@ant-design/icons';
import type { OrderSearchResultDTO } from '@sellsen/generated-sources';
import {
  ORDER_SEARCH_MIN_LENGTH,
  useSearchOrdersAcrossMarketplacesQuery,
} from '@sellsen/services';
import { AutoComplete, Button, Grid, Input, Popover, Typography } from 'antd';
import type { InputRef } from 'antd';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import { CountryFlag } from '../country-flag';
import { openSellsenOrder } from '../../utils';

const { Text } = Typography;

const RESULT_LIMIT = 8;
const DEBOUNCE_MS = 300;
/** Geniş ekranda header'a gömülü kutu; 300px sağ kümeyi menünün üstüne taşırıyordu. */
const INLINE_WIDTH = 260;
/** Dar ekranda popover içindeki kutu; burada yer sorunu yok, tam placeholder sığar. */
const POPOVER_WIDTH = 320;
const FULL_PLACEHOLDER =
  'Tüm mağazalarda sipariş, paket no veya depo sipariş no ara';

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const shortcutLabel = isMac ? '⌘K' : 'Ctrl K';

type SearchOption = {
  value: string;
  label: React.ReactNode;
  marketplace?: string;
};

/**
 * Üst çubuktaki pazarlar arası sipariş araması. Sayfa listesinden bağımsızdır: liste her zaman
 * seçili mağazayı gösterir, bu kutu ise sipariş no / alıcı sipariş no / ASIN / SKU /
 * paket no / depo sipariş no ile tüm mağazalara bakar. Bir sonuç seçilince önce mağaza
 * seçici o siparişin mağazasına çekilir, sonra sipariş detayına gidilir — tepe ile sayfa
 * hiçbir anda çelişmez.
 *
 * ≥1600px'te kutu header'da durur; altında yalnız bir ikon kalır, kutu popover'da açılır.
 */
export const GlobalOrderSearch = () => {
  const { t } = useTranslation();

  const screens = Grid.useBreakpoint();
  // İlk render'da breakpoint'ler bilinmez ({}); geniş kabul edip kutunun zıplamasını önlüyoruz.
  const isWide = screens.xxl !== false;

  const inputRef = useRef<InputRef>(null);
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [debouncedTerm] = useDebounce(term.trim(), DEBOUNCE_MS);

  const hasEnoughChars = debouncedTerm.length >= ORDER_SEARCH_MIN_LENGTH;
  const { data, isFetching } = useSearchOrdersAcrossMarketplacesQuery(
    { q: debouncedTerm, limit: RESULT_LIMIT },
    open && hasEnoughChars
  );

  // ⌘K / Ctrl+K: geniş ekranda kutuya odaklanır; dar ekranda önce popover'ı açar, odak
  // aşağıdaki effect'te verilir. Başka tuşları yutmaz.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        if (isWide) {
          inputRef.current?.focus({ cursor: 'all' });
        } else {
          setPopoverOpen(true);
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isWide]);

  // Popover içeriği açıldığı tick'te henüz mount değil; odak bir sonraki tick'e bırakılır.
  useEffect(() => {
    if (!popoverOpen) {
      return;
    }
    const id = window.setTimeout(
      () => inputRef.current?.focus({ cursor: 'all' }),
      0
    );
    return () => window.clearTimeout(id);
  }, [popoverOpen]);

  const options = useMemo<SearchOption[]>(
    () =>
      (data ?? [])
        .filter((order): order is OrderSearchResultDTO & { amazonOrderId: string } =>
          Boolean(order.amazonOrderId)
        )
        .map((order) => ({
          value: order.amazonOrderId,
          marketplace: order.marketplace,
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '2px 0',
              }}
            >
              <CountryFlag marketplace={order.marketplace} size={18} />
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Text strong style={{ fontSize: 14, lineHeight: '20px' }}>
                  {order.amazonOrderId}
                </Text>
                <Text type="secondary" style={{ fontSize: 12, lineHeight: '18px' }}>
                  {[
                    order.marketplace ? t(order.marketplace) : null,
                    order.asin,
                    order.purchaseDate
                      ? dayjs(order.purchaseDate).format('DD.MM.YYYY')
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </div>
            </div>
          ),
        })),
    [data, t]
  );

  const handleSelect = useCallback(
    (amazonOrderId: string, option: SearchOption) => {
      setTerm('');
      setOpen(false);
      setPopoverOpen(false);
      inputRef.current?.blur();
      openSellsenOrder(amazonOrderId, option.marketplace);
    },
    []
  );

  const notFoundContent = !hasEnoughChars
    ? null
    : isFetching && !data
      ? 'Aranıyor…'
      : options.length === 0
        ? 'Hiçbir mağazada bulunamadı'
        : null;

  const renderSearch = (width: number, inPopover: boolean) => (
    <AutoComplete<string, SearchOption>
      value={term}
      options={options}
      open={open && hasEnoughChars}
      onOpenChange={setOpen}
      onChange={setTerm}
      onSelect={handleSelect}
      notFoundContent={notFoundContent}
      popupMatchSelectWidth={400}
      style={{ width }}
      // Popover içindeyken sonuç listesi body'ye portallanırsa popover onu "dışarı tıklama"
      // sayıp kapanır; liste popover'ın kendi ağacında kalmalı.
      getPopupContainer={
        inPopover
          ? (trigger) => trigger.parentElement ?? document.body
          : undefined
      }
    >
      <Input
        ref={inputRef}
        allowClear
        placeholder={inPopover ? FULL_PLACEHOLDER : 'Sipariş ara'}
        title={FULL_PLACEHOLDER}
        prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
        suffix={
          term ? null : (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {shortcutLabel}
            </Text>
          )
        }
      />
    </AutoComplete>
  );

  if (isWide) {
    return renderSearch(INLINE_WIDTH, false);
  }

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      content={renderSearch(POPOVER_WIDTH, true)}
    >
      <Button
        type="text"
        icon={<SearchOutlined />}
        aria-label={FULL_PLACEHOLDER}
        title={`${FULL_PLACEHOLDER} (${shortcutLabel})`}
      />
    </Popover>
  );
};
